import { TriangleAlert } from 'lucide-react'
import { useId } from 'react'
import { BuilderSection, Note, SECTION_IDS, Toggle } from '@/components/marketplace/maker/BuilderShell.tsx'
import type { BuilderDraft } from '@/components/marketplace/maker/builder-form.ts'
import { useT } from '@/i18n/index.ts'

/**
 * Two toggles, each described by what people will experience.
 *
 * The grounding warning fires on the combination that publishes into a dead end — answer
 * only from your knowledge, with no knowledge attached — and it appears while the maker is
 * still in the section that caused it rather than at the publish step, which is where the
 * server will refuse it (AGM-061).
 */
export const BehaviorSection = ({
  step,
  draft,
  disabled,
  onChange
}: {
  step: number
  draft: BuilderDraft
  disabled: boolean
  onChange: (patch: Partial<BuilderDraft>) => void
}) => {
  // In the body, never at module scope: `const strings = makerStrings.sections.behavior` freezes
  // whichever language was live when the bundle evaluated (PRD-27 F-3).
  const strings = useT().marketplace.maker.sections.behavior
  const fieldId = useId()
  const groundedWithoutKnowledge = draft.grounded && draft.knowledgeBaseIds.length === 0

  return (
    <BuilderSection id={SECTION_IDS.behavior} step={step} title={strings.title} lead={strings.lead}>
      <Toggle
        id={`${fieldId}-grounded`}
        label={strings.groundedLabel}
        on={draft.grounded}
        onDescription={strings.groundedOn}
        offDescription={strings.groundedOff}
        disabled={disabled}
        onChange={(grounded) => onChange({ grounded })}
      />

      {groundedWithoutKnowledge && (
        <Note tone="warn" icon={<TriangleAlert size={13} strokeWidth={2.1} />}>
          {strings.groundedWarning}
        </Note>
      )}

      <Toggle
        id={`${fieldId}-memory`}
        label={strings.memoryLabel}
        on={draft.remembersPreferences}
        onDescription={strings.memoryOn}
        offDescription={strings.memoryOff}
        disabled={disabled}
        onChange={(remembersPreferences) => onChange({ remembersPreferences })}
      />
    </BuilderSection>
  )
}
